// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Transfers.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Gestão de Transferências Bancárias
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Send,
  TrendingUp,
  AccountBalance,
  Public,
  Refresh,
  FileDownload,
  Visibility,
  Undo,
  Cancel,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell, DateCell } from '../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import bankingService, { Transfer, TransferFilters } from '../../services/banking.service';
import { todayCv } from '../../utils/date';

const STATUS_MAP_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
  refunded: 'info',
};

const TYPE_MAP: Record<string, { label: string; icon: React.ReactNode }> = {
  p2p: { label: 'P2P (Pagali)', icon: <Send fontSize="small" /> },
  bank: { label: 'Bancária', icon: <AccountBalance fontSize="small" /> },
  international: { label: 'Internacional', icon: <Public fontSize="small" /> },
};

interface TransferStats {
  total: number;
  totalAmount: number;
  totalFees: number;
  p2p: number;
  bank: number;
  international: number;
}

const BankingTransfers: React.FC = () => {
  const { t } = useTranslation();
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [stats, setStats] = useState<TransferStats>({
    total: 0,
    totalAmount: 0,
    totalFees: 0,
    p2p: 0,
    bank: 0,
    international: 0,
  });

  // Dialog states
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: TransferFilters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(typeFilter !== 'all' && { type: typeFilter }),
      };

      const response = await bankingService.getTransfers(filters);

      setTransfers(response.data);
      setTotalCount(response.pagination?.total || 0);

      if (response.summary) {
        const byType = response.summary.byType || [];
        setStats({
          total: response.summary.totalTransfers || 0,
          totalAmount: response.summary.totalAmount || 0,
          totalFees: response.summary.totalFees || 0,
          p2p: byType.find(t => t.type === 'p2p')?.count || 0,
          bank: byType.find(t => t.type === 'bank')?.count || 0,
          international: byType.find(t => t.type === 'international')?.count || 0,
        });
      }
    } catch (err: any) {
      console.error('Error fetching transfers:', err);
      setError(err.message || t('banking.transfers.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, typeFilter]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const handleRefund = async () => {
    if (!selectedTransfer || !refundReason) return;

    setActionLoading(true);
    try {
      await bankingService.refundTransfer(selectedTransfer.id, refundReason);
      setRefundOpen(false);
      setRefundReason('');
      fetchTransfers();
    } catch (err: any) {
      setError(err.message || t('banking.transfers.errorRefund'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await bankingService.exportTransfers({
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(typeFilter !== 'all' && { type: typeFilter }),
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transferencias_${todayCv()}.csv`;
      a.click();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const columns: Column<Transfer>[] = [
    {
      id: 'id',
      label: 'ID',
      format: (_, row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {row.id.slice(0, 8)}...
        </Typography>
      ),
    },
    {
      id: 'type',
      label: 'Tipo',
      format: (_, row) => {
        const type = TYPE_MAP[row.type] || { label: row.type, icon: null };
        return (
          <Chip
            size="small"
            icon={type.icon as any}
            label={type.label}
            variant="outlined"
          />
        );
      },
    },
    {
      id: 'senderName',
      label: 'Remetente',
      format: (_, row) => (
        <Box>
          <Typography variant="body2">{row.senderName}</Typography>
          {row.senderPhone && (
            <Typography variant="caption" color="text.secondary">
              {row.senderPhone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'recipientName',
      label: 'Destinatário',
      format: (_, row) => (
        <Box>
          <Typography variant="body2">{row.recipientName}</Typography>
          {row.recipientPhone && (
            <Typography variant="caption" color="text.secondary">
              {row.recipientPhone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'amount',
      label: 'Valor',
      align: 'right',
      format: (_, row) => <CurrencyCell value={row.amount} />,
    },
    {
      id: 'fee',
      label: 'Taxa',
      align: 'right',
      format: (_, row) => (
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(row.fee)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Estado',
      format: (_, row) => {
        const statusKey = `banking.transfers.status${row.status.charAt(0).toUpperCase() + row.status.slice(1)}`;
        return <Chip label={t(statusKey)} size="small" color={STATUS_MAP_COLORS[row.status] || 'default'} />;
      },
    },
    {
      id: 'createdAt',
      label: 'Data',
      format: (_, row) => <DateCell value={row.createdAt} />,
    },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center',
      format: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver detalhes">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedTransfer(row);
                setDetailsOpen(true);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status === 'completed' && (
            <Tooltip title="Reembolsar">
              <IconButton
                size="small"
                color="warning"
                onClick={() => {
                  setSelectedTransfer(row);
                  setRefundOpen(true);
                }}
              >
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          {t('banking.transfers.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<FileDownload />} variant="outlined" onClick={handleExport}>
            {t('common.export')}
          </Button>
          <Button startIcon={<Refresh />} variant="contained" onClick={fetchTransfers}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp color="primary" />
                <Typography color="text.secondary">Total</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(stats.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Send color="info" />
                <Typography color="text.secondary">P2P</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.p2p}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalance color="success" />
                <Typography color="text.secondary">{t('banking.transfers.bankTransfers')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.bank}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Public color="warning" />
                <Typography color="text.secondary">{t('banking.transfers.international')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.international}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Pesquisar"
                placeholder="Nome, telefone, ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="p2p">P2P (Pagali)</MenuItem>
                <MenuItem value="bank">Bancária</MenuItem>
                <MenuItem value="international">Internacional</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="processing">Processando</MenuItem>
                <MenuItem value="completed">Concluído</MenuItem>
                <MenuItem value="failed">Falhou</MenuItem>
                <MenuItem value="refunded">Reembolsado</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={transfers}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('banking.transfers.details')}</DialogTitle>
        <DialogContent>
          {selectedTransfer && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {selectedTransfer.id}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Tipo</Typography>
                <Typography variant="body2">
                  {TYPE_MAP[selectedTransfer.type]?.label || selectedTransfer.type}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Remetente</Typography>
                <Typography variant="body2">{selectedTransfer.senderName}</Typography>
                <Typography variant="caption">{selectedTransfer.senderPhone}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Destinatário</Typography>
                <Typography variant="body2">{selectedTransfer.recipientName}</Typography>
                <Typography variant="caption">{selectedTransfer.recipientPhone}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Valor</Typography>
                <Typography variant="h6">{formatCurrency(selectedTransfer.amount)}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Taxa</Typography>
                <Typography variant="body2">{formatCurrency(selectedTransfer.fee)}</Typography>
              </Grid>
              {selectedTransfer.type === 'international' && (
                <>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Taxa de Câmbio</Typography>
                    <Typography variant="body2">{selectedTransfer.exchangeRate}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Valor Destino</Typography>
                    <Typography variant="body2">
                      {selectedTransfer.destinationAmount} {selectedTransfer.destinationCurrency}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">Descrição</Typography>
                <Typography variant="body2">{selectedTransfer.description || '-'}</Typography>
              </Grid>
              {selectedTransfer.failureReason && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">{selectedTransfer.failureReason}</Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundOpen} onClose={() => setRefundOpen(false)}>
        <DialogTitle>{t('banking.transfers.refund')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('banking.transfers.confirmRefund')}{' '}
            <strong>{selectedTransfer && formatCurrency(selectedTransfer.amount)}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('banking.transfers.refundReason')}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOpen(false)} disabled={actionLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRefund}
            disabled={actionLoading || !refundReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : t('banking.transfers.refundAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankingTransfers;
